import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronRightIcon, HomeIcon } from '@heroicons/react/24/outline';
import { cn } from '@/utils/cn';
import { useBreadcrumb } from '@/contexts/BreadcrumbContext';
import { SECURITY_DASHBOARD_PATH } from '@/config/relocatedRoutes';

interface BreadcrumbItem {
  label: string;
  href?: string;
  current?: boolean;
  path?: string; // Full path for override lookup
}

const routeLabels: Record<string, string> = {
  dashboard: 'Dashboard',
  incidents: 'Incidents',
  'executive-protection': 'Executive Protection',
  threats: 'Threat Intelligence',
  investigations: 'Investigations',
  subjects: 'Subjects',
  analytics: 'Analytics',
  settings: 'Settings',
  profile: 'Profile',
  notifications: 'Notifications',
  activities: 'Activities',
  tracking: 'Live Tracking',
  reports: 'Reports',
  create: 'Create',
  edit: 'Edit',
  feeds: 'Feed Item',
  manage: 'Manage Feeds',
  // Module names
  security: 'Corporate Security',
  legal: 'Legal & Compliance',
  // L&C sections
  vendors: 'Vendor Management',
  matters: 'Legal Matters',
  governance: 'Governance',
  compliance: 'Compliance',
  policies: 'Policies',
  regulatory: 'Regulatory',
};

// Paths that exist as intermediate segments but don't have their own routable pages
// These will be displayed as text (not links) in breadcrumbs
const nonRoutablePaths = new Set([
  '/threats/feeds',
  '/security/threats/feeds',
]);

// Module base paths — these are navigable section headers in breadcrumbs.
// VIS-150: the Corporate Security module's default sub-route is its dashboard.
const moduleBasePaths: Record<string, string> = {
  '/security': '/security/dashboard',
  '/legal': '/legal/dashboard',
};

const generateBreadcrumbs = (pathname: string): BreadcrumbItem[] => {
  const paths = pathname.split('/').filter(Boolean);
  const breadcrumbs: BreadcrumbItem[] = [];

  // Always start with the (relocated) security dashboard, except when already on it.
  if (paths.length > 0 && pathname !== SECURITY_DASHBOARD_PATH) {
    breadcrumbs.push({
      label: 'Dashboard',
      href: SECURITY_DASHBOARD_PATH,
    });
  }

  let currentPath = '';
  paths.forEach((path, index) => {
    currentPath += `/${path}`;
    const isLast = index === paths.length - 1;

    // Check if it's a dynamic route (like incident ID, UUID, or CUID)
    const isDynamicRoute = /^[A-Z]{2,}-\d{4}-\d{3,}$/.test(path) ||
                          /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(path) ||
                          /^c[a-z0-9]{20,}$/i.test(path);

    if (isDynamicRoute) {
      // For dynamic routes, store the full path so we can look up overrides
      const previousPath = paths[index - 1];
      const baseName = routeLabels[previousPath] || previousPath;
      breadcrumbs.push({
        label: `${baseName} Details`,
        current: isLast,
        path: currentPath, // Store full path for override lookup
      });
    } else {
      const label = routeLabels[path] || path.charAt(0).toUpperCase() + path.slice(1);
      // Don't add href for non-routable paths or if it's the last segment
      const shouldLink = !isLast && !nonRoutablePaths.has(currentPath);
      // For module base paths, link to their default page
      const href = shouldLink
        ? (moduleBasePaths[currentPath] ?? currentPath)
        : undefined;
      breadcrumbs.push({
        label,
        href,
        current: isLast,
      });
    }
  });

  return breadcrumbs;
};

interface BreadcrumbsProps {
  className?: string;
}

export default function Breadcrumbs({ className }: BreadcrumbsProps) {
  const location = useLocation();
  const { getOverride } = useBreadcrumb();
  const breadcrumbs = generateBreadcrumbs(location.pathname);

  // Apply any overrides from the context
  const breadcrumbsWithOverrides = breadcrumbs.map(breadcrumb => {
    if (breadcrumb.path) {
      const override = getOverride(breadcrumb.path);
      if (override) {
        return { ...breadcrumb, label: override };
      }
    }
    return breadcrumb;
  });

  // Don't show breadcrumbs on the module dashboard or login page.
  if (location.pathname === SECURITY_DASHBOARD_PATH || location.pathname === '/login') {
    return null;
  }

  return (
    <nav
      className={cn(
        'flex items-center space-x-1 text-sm text-secondary-600 dark:text-secondary-400',
        className
      )}
      aria-label="Breadcrumb"
    >
      <Link
        to={SECURITY_DASHBOARD_PATH}
        className="flex items-center hover:text-secondary-900 dark:hover:text-secondary-200 transition-colors"
        aria-label="Go to dashboard"
      >
        <HomeIcon className="w-4 h-4" />
      </Link>

      {breadcrumbsWithOverrides.map((breadcrumb, index) => (
        <React.Fragment key={index}>
          <ChevronRightIcon className="w-4 h-4 text-secondary-400 dark:text-secondary-600" />
          {breadcrumb.href ? (
            <Link
              to={breadcrumb.href}
              className={cn(
                'hover:text-secondary-900 dark:hover:text-secondary-200 transition-colors',
                'truncate max-w-[200px] sm:max-w-none'
              )}
            >
              {breadcrumb.label}
            </Link>
          ) : (
            <span
              className={cn(
                'font-medium',
                breadcrumb.current
                  ? 'text-secondary-900 dark:text-secondary-100'
                  : 'text-secondary-600 dark:text-secondary-400',
                'truncate max-w-[200px] sm:max-w-none'
              )}
              aria-current={breadcrumb.current ? 'page' : undefined}
            >
              {breadcrumb.label}
            </span>
          )}
        </React.Fragment>
      ))}
    </nav>
  );
}
